import React, { useState } from "react";
import { Icon, TIconName  } from "@ceridianhcm/components";
import styles from "./navbar.module.scss";

enum NavbarColorSchemes {
    primary,
    link,
    info,
    success,
    warning,
    danger,
    white,
    light,
    dark,
    black
}

interface IBaseNavbarItemProps {
    href?: string,
    icon?: TIconName,
    title: string,
    onClick?: () => void,
    disabled?: boolean
}

interface INabarItemChildProps extends IBaseNavbarItemProps {
    hasDivider?: boolean
}

interface INavbarItemProps extends IBaseNavbarItemProps {
    active?: boolean,
    seperator?: boolean,
    children?: IBaseNavbarItemProps[] | null
}


class NavbarItem {
    #active: boolean;
    #seperator: boolean;
    #href: string | null;
    #icon: TIconName | null;
    #title: string;
    #onClick: (() => void) | null;
    #children: INabarItemChildProps[] | null;
    #disabled: boolean;
    constructor(props: INavbarItemProps) {
        this.#active = (props.active === undefined || props.active === null ? false : props.active!);
        this.#seperator = (props.seperator === undefined || props.seperator === null ? false : props.seperator!);
        this.#title = props.title;
        this.#href = (props.href === undefined || props.href === null ? null : props.href!);
        this.#icon = (props.icon === undefined || props.icon === null ? null : props.icon!);
        this.#onClick = (props.onClick === undefined || props.onClick === null ? null : props.onClick!);
        this.#children = (props.children === undefined || props.children === null ? null : props.children!);
        this.#disabled = (props.disabled === undefined || props.disabled === null ? false : props.disabled!);
    }
    get Active(): boolean { return this.#active; }
    set Active(value: boolean) { this.#active = value; }
    get Seperator(): boolean { return this.#seperator; }
    get Title() { return this.#title; }
    get HRef() { return this.#href; }
    get Icon() { return this.#icon; }
    get onClick() { return this.#onClick; }
    get Children() { return this.#children; }
    get Disabled() { return this.#disabled; }
    set Disabled(value: boolean) { this.#disabled = value; }
}

const Navbar: React.FC<{
    colorScheme?: NavbarColorSchemes | null,
    brandImage?: string | null,
    brandURL?: string | null,
    startItems?: NavbarItem[] | null,
    endItems?: NavbarItem[] | null,
    testId?:string
}> = ({
    colorScheme,
    brandImage,
    brandURL,
    startItems,
    endItems,
    testId
}) => {
        const [isVisible, setIsVisible] = useState<boolean>(false);

        const RenderItem = (item: NavbarItem, index: number) => {
            if (item.Children === null) {
                return (
                    <a key={index} className={`${styles.navbar_item} ${item.Active ? styles.is_active : ''} ${item.Seperator ? styles.has_seperator : ''} ${item.Disabled ? styles.is_disabled : ''}`} href={(item.HRef === null || item.Disabled ? undefined : item.HRef)} onClick={() => { if (!item.Disabled) { setIsVisible(false); if (item.onClick !== undefined && item.onClick !== null) { item.onClick(); } } }}>
                        {item.Icon !== null ? <Icon name={item.Icon} /> : null}
                        {item.Title}
                    </a>
                );
            } else {
                let children: any[] = [];
                item.Children.forEach((value: INabarItemChildProps, idx: number) => {
                    children.push((<a key={`${index}_${idx}`} className={`${styles.navbar_item} ${value.disabled !== undefined && value.disabled ? styles.is_disabled : ''}`} href={value.disabled !== undefined && value.disabled ? undefined : value.href} onClick={() => { if (!(value.disabled !== undefined && value.disabled)) { setIsVisible(false); if (value.onClick !== undefined && value.onClick !== null) { value.onClick(); } } }}>
                        {value.icon !== undefined && value.icon !== null ? <Icon name={value.icon!} /> : null}
                        {value.title}
                    </a>));
                    if (value.hasDivider) {
                        children.push((<hr key={`${index}_${idx}_div`} className={styles.navbar_divider} />));
                    }
                });
                return (
                    <div key={index} className={`${styles.navbar_item} ${styles.has_dropdown} ${styles.is_hoverable} ${item.Active ? styles.is_active : ''} ${item.Seperator ? styles.has_seperator : ''} ${item.Disabled ? styles.is_disabled : ''}`}>
                        <a className={`${styles.navbar_link} ${item.Disabled ? styles.is_disabled : ''}`}>
                            {item.Icon !== null ? <Icon name={item.Icon} /> : null}
                            {item.Title}
                        </a>
                        <div className={styles.navbar_dropdown}>
                            {children.map((child: any) => { return child; })}
                        </div>
                    </div>
                );
            }
        };

        let start = null;
        let end = null;
        let className = styles.navbar;
        if (colorScheme !== null) {
            switch (colorScheme!) {
                case NavbarColorSchemes.primary:
                    className += ' ' + styles.is_primary;
                    break;
                case NavbarColorSchemes.link:
                    className += ' ' + styles.is_link;
                    break;
                case NavbarColorSchemes.info:
                    className += ' ' + styles.is_info;
                    break;
                case NavbarColorSchemes.success:
                    className += ' ' + styles.is_success;
                    break;
                case NavbarColorSchemes.warning:
                    className += ' ' + styles.is_warning;
                    break;
                case NavbarColorSchemes.danger:
                    className += ' ' + styles.is_danger;
                    break;
                case NavbarColorSchemes.white:
                    className += ' ' + styles.is_white;
                    break;
                case NavbarColorSchemes.light:
                    className += ' ' + styles.is_light;
                    break;
                case NavbarColorSchemes.dark:
                    className += ' ' + styles.is_dark;
                    break;
                case NavbarColorSchemes.black:
                    className += ' ' + styles.is_black;
                    break;

            }
        }
        if (startItems !== undefined && startItems !== null) {
            start = (<div data-testid={(testId===undefined ? undefined : testId+'-start')} className={styles.navbar_start}>{startItems.map((value: NavbarItem, index: number) => { return RenderItem(value, index); })}</div>);
        }
        if (endItems !== undefined && endItems !== null) {
            end = (<div  data-testid={(testId===undefined ? undefined : testId+'-end')} className={styles.navbar_end}>{endItems.map((value: NavbarItem, index: number) => { return RenderItem(value, index); })}</div>);
        }
        return (
            <nav className={className} data-testid={testId}>
                <div className={styles.navbar_brand}>
                    {brandImage ?
                        <a  data-testid={(testId===undefined ? undefined : testId+'-brand-link')} className={styles.navbar_item} href={brandURL === undefined || brandURL === null ? undefined : brandURL}>
                            <img src={brandImage} data-testid={(testId===undefined ? undefined : testId+'-brand')} />
                        </a>
                        : null
                    }
                    <div data-testid={(testId===undefined ? undefined : testId+'-burger')} className={styles.navbar_burger} onClick={() => setIsVisible(!isVisible)}>
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>
                <div data-testid={(testId===undefined ? undefined : testId+'-menu')} className={`${styles.navbar_menu} ${isVisible ? styles.is_active : ''}`}>
                    {start}
                    {end}
                </div>
            </nav>
        );
    };

export { Navbar, NavbarItem, NavbarColorSchemes };